import {createSelector} from '@reduxjs/toolkit';

import {LUXURY_BADGES} from 'constants/badges';
import {PRODUCT_TYPES} from 'constants/product';
import {ApplicationStoreType} from 'desktop/store';
import {
    CheckoutItemsType,
    CheckoutItemType,
    OrderReviewType,
} from 'desktop/types/checkoutItemsType';

import {MODULE_KEY_CHECKOUT} from '../constants';

/**
 * Get order review items
 * @param {ApplicationStoreType} store - current store
 * @return {CheckoutItemsType} - order review items
 */
export const getOrderReviewItemsData = (store: ApplicationStoreType): OrderReviewType =>
    store[MODULE_KEY_CHECKOUT].orderReviewItems;

/**
 * Get order review items
 * @param {ApplicationStoreType} store - current store
 * @return {CheckoutItemsType} - order review items
 */
export const getOrderReviewItems = (store: ApplicationStoreType): CheckoutItemsType =>
    store[MODULE_KEY_CHECKOUT].orderReviewItems.list;

/**
 * Returns true if there is at least one UHC item on checkout
 * @param {ApplicationStoreType} store - store
 * @returns {boolean} result
 */
export const isSomeUhcItems = createSelector(getOrderReviewItems, (quoteItems: CheckoutItemsType) =>
    quoteItems.some((item: CheckoutItemType) => item.isAppliedBenefits),
);

/**
 * Get if there is at least one of the order items has a subscription
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - whether at least one of the quote items has a subscription
 */
export const getIsOrderHasActiveSubscriptionCL = createSelector(
    getOrderReviewItems,
    (items: CheckoutItemsType) =>
        items
            .filter(item => item.active === true)
            .some(item => Boolean(item.subscription?.subscriptionMode)),
);

/**
 * Get is order review items loaded
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - is status "fulfilled"
 */
export const getIsOrderReviewItemsLoaded = (store: ApplicationStoreType): boolean =>
    store[MODULE_KEY_CHECKOUT].orderReviewItems.status === 'FULFILLED';

/**
 * Contact Lenses In Order Review
 */
export const isContactLensesInOrderReview = createSelector(getOrderReviewItems, items =>
    items.some(({product}) => product.type === PRODUCT_TYPES.CONTACT_LENSES),
);

/**
 * Is Only Contact Lenses In Order Review
 */
export const isOnlyContactLensesInOrderReview = createSelector(getOrderReviewItems, items =>
    items.every(({product}) => product.type === PRODUCT_TYPES.CONTACT_LENSES),
);

/**
 * Get item from order review by id
 * @param {number} itemId - item id
 * @returns {CheckoutItemType} - item with specified id by props
 */
export const getOrderReviewItemById = (itemId: number) =>
    createSelector(getOrderReviewItems, items => items.find(item => item.id === itemId));

/**
 * Get item from order review by id
 * @param {number} itemId - item id
 * @returns {CheckoutItemType} - item with specified id by props
 */
export const getIsVista = createSelector(getOrderReviewItems, items =>
    items.some(({product}) => product?.brand?.label === 'Vista Plus'),
);

/**
 * Returns if there is an item with the ottica luxury experience badge in the cart
 */
export const getIsCartHasLuxuryItem = createSelector(getOrderReviewItems, items =>
    items.some(item =>
        item.product.product_badge.some(badge => LUXURY_BADGES.includes(Number(badge))),
    ),
);
